import { forwardRef, type ComponentPropsWithoutRef } from 'react'
import { NumericFormat, type NumericFormatProps } from 'react-number-format'

import { cn } from './cn'
import { useControlProps, useQuietField } from './control-scope'
import { controlShell, EMPTY_MARK, Field } from './Field'

type NumberInputProps = Omit<
  NumericFormatProps<ComponentPropsWithoutRef<'input'>>,
  'value' | 'onChange' | 'onValueChange' | 'customInput' | 'className'
> & {
  value: number | null | undefined
  /** Empty input reads as `null`, so a cleared field is not zero. */
  onValueChange?: (value: number | null) => void
  label?: string
  helper?: string
  error?: string
  className?: string
  /** Datasheet row: no box until hover or focus. */
  quiet?: boolean
  /**
   * Where the figure sits in the box. `right` is for a table column of
   * numbers, where a left-set value in the one editable cell breaks the column
   * the eye runs down.
   */
  align?: 'left' | 'right'
}

/**
 * A number, written the German way: `.` groups thousands and `,` is the
 * decimal mark.
 *
 * The separators live here rather than at the call sites, so a figure reads
 * the same in the table, the form and the PDF.
 */
export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  function NumberInput(
    {
      value,
      onValueChange,
      label,
      helper,
      error,
      className,
      quiet: quietProp,
      align = 'left',
      onFocus,
      ...rest
    },
    ref,
  ) {
    const quiet = useQuietField(quietProp)
    const control = useControlProps(rest)

    return (
      <Field label={label} helper={helper} error={error} className={className}>
        {field => (
          <NumericFormat
            getInputRef={ref}
            // `NumericFormat` writes into a text input, because it formats
            // while the visitor types and `type="number"` rejects the group
            // separators. A phone then offers the full keyboard unless it is
            // told what is being asked for.
            inputMode={rest.decimalScale === 0 ? 'numeric' : 'decimal'}
            {...field}
            placeholder={quiet ? EMPTY_MARK : undefined}
            thousandSeparator="."
            decimalSeparator=","
            value={value ?? ''}
            onValueChange={next => onValueChange?.(next.floatValue ?? null)}
            className={controlShell(
              quiet,
              cn('tabular-nums', align === 'right' && 'text-right'),
            )}
            {...control}
            // `NumericFormat` swallows `onFocus` and re-fires it from a
            // `setTimeout`, to place the caret. A field that tracks "focused
            // since" needs it before the first keystroke, and the capture
            // phase is both synchronous and forwarded untouched.
            onFocusCapture={onFocus}
          />
        )}
      </Field>
    )
  },
)

/** A number in euros. */
export const PriceInput = forwardRef<HTMLInputElement, NumberInputProps>(
  function PriceInput(props, ref) {
    return (
      <NumberInput
        ref={ref}
        decimalScale={2}
        fixedDecimalScale
        suffix=" €"
        {...props}
      />
    )
  },
)
